import {
    useEffect,
    type FC
} from "react";
import {
    type NativeSyntheticEvent,
    type TargetedEvent
} from "react-native";
import type ISidebarMenuActionProps from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSidebarRootContext,
    useSidebarItemContext
} from "../../context";
import {
    Ellipsis as EllipsisIcon
} from "lucide-react-native";
import {
    getSidebarActionPlacement,
    getSidebarRowMeasures,
    getSidebarRowRadius,
    getSidebarMeasures
} from "../../stylesheet";
import {
    getSidebarActionRadius
} from "../../util";
import SidebarIconButton from "../iconButton";

const SidebarMenuActionIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <EllipsisIcon
    color={customColor}
    size={size}
/>;

const SidebarMenuAction: FC<ISidebarMenuActionProps> = ({
    customTheme: customThemeProp,
    icon = SidebarMenuActionIcon,
    isShowOnHover = true,
    onFocus: onFocusProp,
    isDisabled = false,
    onBlur: onBlurProp,
    style,
    title,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.MenuAction"
    });

    const item = useSidebarItemContext();

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const isExpanded = props["aria-expanded"] === true;

    useEffect(() => {
        item?.setHasAction(true);

        return () => {
            item?.setHasAction(false);
        };
    }, [
        item?.setHasAction
    ]);

    useEffect(() => {
        item?.setIsActionOpen(isExpanded);
    }, [
        item?.setIsActionOpen,
        isExpanded
    ]);

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const rowMeasures = getSidebarRowMeasures({
        size: item?.size ?? "medium",
        square: measures.square,
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes
    });

    const placement = getSidebarActionPlacement({
        height: rowMeasures.height,
        inset: rowMeasures.inset,
        touchTargets,
        pointerType,
        iconSizes,
        spaces
    });

    const rowRadius = getSidebarRowRadius({
        variant: root?.variant ?? "sidebar",
        measures,
        radiuses,
        borders
    });

    const radius = getSidebarActionRadius({
        inset: (rowMeasures.height - placement.visualSize) / 2,
        outer: rowRadius,
        radiuses
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        height: rowMeasures.height,
        placement
    });

    const onFocus = (event: NativeSyntheticEvent<TargetedEvent>) => {
        item?.setIsFocused(true);

        onFocusProp?.(event);
    };

    const onBlur = (event: NativeSyntheticEvent<TargetedEvent>) => {
        item?.setIsFocused(false);

        onBlurProp?.(event);
    };

    if(root?.isCollapsed) {
        return null;
    }

    return <SidebarIconButton
        {...props}
        isVisible={!isShowOnHover || pointerType === "coarse" || (item?.isActionVisible ?? true) || isExpanded}
        isActiveRow={item?.isActive}
        style={[
            style,
            containerDynamicStyle
        ]}
        customTheme={customTheme}
        isDisabled={isDisabled}
        placement={placement}
        onFocus={onFocus}
        onBlur={onBlur}
        radius={radius}
        title={title}
        icon={icon}
    />;
};
export default SidebarMenuAction;
